/**
 * generateText - Enhanced generateText with Ollama-specific reliability
 *
 * This wrapper provides response synthesis and enhanced tool calling reliability
 * that addresses the core Ollama limitation: tools execute but no final text is generated.
 */

import { generateText as _generateText, stepCountIs } from 'ai';

/**
 * Create a new object that inherits from `base` (preserving its getters/prototype)
 * but with specific properties overridden via own-property descriptors.
 */
function resultWithOverrides<T extends object>(
  base: T,
  overrides: Partial<T>,
): T {
  const descriptors: PropertyDescriptorMap = {};
  for (const key of Object.keys(overrides)) {
    descriptors[key] = {
      value: (overrides as Record<string, unknown>)[key],
      enumerable: true,
      configurable: true,
    };
  }
  return Object.create(base, descriptors) as T;
}

/**
 * Enhanced options for Ollama-specific reliability features
 */
export interface EnhancedOptions {
  /**
   * Enable response synthesis when tools are called but no text is generated
   * @default true
   */
  enableSynthesis?: boolean;

  /**
   * Custom synthesis prompt template
   */
  synthesisPrompt?: string;

  /**
   * Maximum attempts for synthesis
   * @default 2
   */
  maxSynthesisAttempts?: number;

  /**
   * Minimum response length to consider valid
   * @default 10
   */
  minResponseLength?: number;

  /**
   * EXPERIMENTAL: Enable tool calling with structured output (output)
   *
   * The official AI SDK doesn't support combining toolChoice: 'required' with output.
   * When enabled, this uses a two-phase approach:
   * 1. Execute tools first (without output)
   * 2. Generate structured output with tool results injected as context
   *
   * This is NOT standard AI SDK behavior - only enable if you need both features together.
   *
   * @default false
   */
  enableToolsWithStructuredOutput?: boolean;
}

/**
 * Enhanced generateText options that extend the official AI SDK options
 */
export type GenerateTextOptions = Parameters<typeof _generateText>[0] & {
  /**
   * Enhanced options for Ollama-specific reliability features
   */
  enhancedOptions?: EnhancedOptions;
};

/**
 * Enhanced generateText function with Ollama-specific reliability improvements
 *
 * This function applies synthesis by default when tools execute but return empty responses.
 * The enhancement preserves the original response prototype and all methods/getters.
 *
 * Type parameters are inferred from the options, preserving AI SDK's type inference.
 */
export async function generateText(
  options: GenerateTextOptions,
): Promise<Awaited<ReturnType<typeof _generateText>>> {
  const { enhancedOptions = {}, ...generateTextOptions } = options;

  const {
    enableSynthesis = true,
    synthesisPrompt = 'Based on the tool results above, please provide a comprehensive response to the original question.',
    maxSynthesisAttempts = 2,
    minResponseLength = 10,
    enableToolsWithStructuredOutput = false,
  } = enhancedOptions;

  const hasTools =
    generateTextOptions.tools &&
    Object.keys(generateTextOptions.tools).length > 0;

  // EXPERIMENTAL: Support tools with structured output (opt-in only)
  const hasOutput = 'output' in generateTextOptions;
  const requiresTools =
    generateTextOptions.toolChoice === 'required' ||
    (typeof generateTextOptions.toolChoice === 'object' &&
      'type' in generateTextOptions.toolChoice &&
      generateTextOptions.toolChoice.type === 'tool');

  if (
    enableToolsWithStructuredOutput &&
    hasOutput &&
    requiresTools &&
    hasTools
  ) {
    // Phase 1: Execute tools without output (omit the key entirely)
    const phase1Options = Object.fromEntries(
      Object.entries(generateTextOptions).filter(([key]) => key !== 'output'),
    );
    const toolResult = await _generateText(
      phase1Options as Parameters<typeof _generateText>[0],
    );

    // If tools were called, use their results in Phase 2
    if (toolResult.toolCalls && toolResult.toolCalls.length > 0) {
      // Build context with tool results
      const toolContext = toolResult.toolResults
        ?.map(
          (tr, index) =>
            `${toolResult.toolCalls?.[index]?.toolName}: ${JSON.stringify(tr.output ?? tr)}`,
        )
        .join('\n');

      const toolResultsSuffix = `\n\nTool Results:\n${toolContext}\n\nPlease provide a structured response based on these tool results.`;

      // Inject tool context into either prompt or messages
      const phase2Base = Object.fromEntries(
        Object.entries(generateTextOptions).filter(
          ([key]) => key !== 'tools' && key !== 'toolChoice',
        ),
      ) as typeof generateTextOptions;
      const phase2OptionsWithPrompt =
        typeof phase2Base.prompt === 'string'
          ? { ...phase2Base, prompt: phase2Base.prompt + toolResultsSuffix }
          : phase2Base.messages
            ? {
                ...phase2Base,
                prompt: undefined,
                messages: [
                  ...phase2Base.messages,
                  { role: 'user' as const, content: toolResultsSuffix.trim() },
                ],
              }
            : phase2Base;

      // Phase 2: Generate structured output with tool context
      const structuredResult = await _generateText(
        phase2OptionsWithPrompt as Parameters<typeof _generateText>[0],
      );

      // Merge tool metadata into structured result (without mutating readonly getters)
      const enhancedResult = resultWithOverrides(structuredResult, {
        toolCalls: toolResult.toolCalls,
        toolResults: toolResult.toolResults,
        staticToolCalls: toolResult.staticToolCalls,
        dynamicToolCalls: toolResult.dynamicToolCalls,
        staticToolResults: toolResult.staticToolResults,
        dynamicToolResults: toolResult.dynamicToolResults,
        usage: {
          inputTokens:
            (toolResult.usage.inputTokens ?? 0) +
            (structuredResult.usage.inputTokens ?? 0),
          outputTokens:
            (toolResult.usage.outputTokens ?? 0) +
            (structuredResult.usage.outputTokens ?? 0),
          totalTokens:
            (toolResult.usage.totalTokens ?? 0) +
            (structuredResult.usage.totalTokens ?? 0),
        },
      } as Partial<typeof structuredResult>);

      return enhancedResult;
    }
  }

  // First, try standard generateText with multi-turn tool calling enabled
  // Automatically forward all AI SDK options, only override stopWhen if needed
  const result = await _generateText({
    ...(generateTextOptions as Parameters<typeof _generateText>[0]),
    // Only set stopWhen default if user didn't provide one and tools are enabled
    stopWhen: (generateTextOptions.stopWhen ??
      (hasTools ? stepCountIs(5) : undefined)) as Parameters<
      typeof _generateText
    >[0]['stopWhen'],
  });

  // Check if we need synthesis (tools called but no meaningful text)
  // Check across all steps to see if any tools were called
  const toolsWereCalled =
    result.steps?.some(
      (step: { toolCalls?: unknown[] }) =>
        step.toolCalls && step.toolCalls.length > 0,
    ) ?? false;
  const hasMinimalText =
    !result.text || result.text.trim().length < minResponseLength;

  if (!hasTools || !toolsWereCalled || !hasMinimalText || !enableSynthesis) {
    return result;
  }

  // Attempt synthesis with tool results
  for (let attempt = 1; attempt <= maxSynthesisAttempts; attempt++) {
    try {
      // Build synthesis prompt with tool context from all steps
      const allToolCalls: Array<{ toolName: string; result: unknown }> = [];

      if (result.steps) {
        for (const step of result.steps) {
          if (step.toolCalls && step.toolResults) {
            for (let index = 0; index < step.toolCalls.length; index++) {
              const toolCall = step.toolCalls[index];
              const toolResult = step.toolResults[index];
              if (toolCall && toolResult) {
                allToolCalls.push({
                  toolName: toolCall.toolName,
                  result: toolResult,
                });
              }
            }
          }
        }
      }

      const toolContext =
        allToolCalls
          .map((tc) => `${tc.toolName}: ${JSON.stringify(tc.result)}`)
          .join('\n') || '';

      const originalPrompt =
        typeof options.prompt === 'string'
          ? options.prompt
          : options.messages?.at(-1)?.content || 'the user question';

      const fullSynthesisPrompt = `Original request: ${originalPrompt}

Tool results:
${toolContext}

${synthesisPrompt}`;

      const generateOptions = generateTextOptions as Parameters<
        typeof _generateText
      >[0];
      const baseOptions = Object.fromEntries(
        Object.entries(generateOptions).filter(
          ([key]) => key !== 'tools' && key !== 'prompt' && key !== 'messages',
        ),
      );

      // Use messages pattern if original call used messages, otherwise use prompt
      const synthesisOptions = generateOptions.messages
        ? {
            ...baseOptions,
            messages: [
              ...(generateOptions.messages || []),
              { role: 'user' as const, content: fullSynthesisPrompt },
            ],
          }
        : {
            ...baseOptions,
            prompt: fullSynthesisPrompt,
          };

      // Generate synthesis response
      const synthesisResult = await _generateText(
        synthesisOptions as Parameters<typeof _generateText>[0],
      );

      if (
        synthesisResult.text &&
        synthesisResult.text.trim().length >= minResponseLength
      ) {
        // Merge synthesis text and combined usage into result (without mutating readonly getters)
        const enhancedResult = resultWithOverrides(result, {
          text: synthesisResult.text,
          usage: {
            inputTokens:
              (result.usage.inputTokens ?? 0) +
              (synthesisResult.usage.inputTokens ?? 0),
            outputTokens:
              (result.usage.outputTokens ?? 0) +
              (synthesisResult.usage.outputTokens ?? 0),
            totalTokens:
              (result.usage.totalTokens ?? 0) +
              (synthesisResult.usage.totalTokens ?? 0),
          },
        } as Partial<typeof result>);

        return enhancedResult;
      }
    } catch {
      // Silently continue to next attempt
    }
  }

  return result;
}
